import {
	Archive01Icon,
	DeliveryBox01Icon,
	DeliveryTruck02Icon,
	File02Icon,
	ShoppingBasket01Icon,
	Store01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { LayoutPanelTop } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { BranchSelector } from "@/components/branch-selector";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { AddPurchaseOrderItemModal } from "@/components/modals/add-purchase-order-item-modal";
import { AddRestockItemModal } from "@/components/modals/add-restock-item-modal";
import { AddSupplierOrderItemModal } from "@/components/modals/add-supplier-order-item-modal";
import { AddTransferItemModal } from "@/components/modals/add-transfer-item-modal";
import { AdjustStockModal } from "@/components/modals/adjust-stock-modal";
import { CreatePurchaseOrderModal } from "@/components/modals/create-purchase-order-modal";
import { CreateRestockModal } from "@/components/modals/create-restock-modal";
import { CreateSupplierOrderModal } from "@/components/modals/create-supplier-order-modal";
import { CreateTransferOrderModal } from "@/components/modals/create-transfer-order-modal";
import { EditRestockItemModal } from "@/components/modals/edit-restock-item-modal";
import { EditRestockModal } from "@/components/modals/edit-restock-modal";
import { EditSupplierOrderItemModal } from "@/components/modals/edit-supplier-order-item-modal";
import { RecordCountModal } from "@/components/modals/record-count-modal";
import { RejectPurchaseOrderModal } from "@/components/modals/reject-purchase-order-modal";
import { RejectReconciliationModal } from "@/components/modals/reject-reconciliation-modal";
import { StartReconciliationModal } from "@/components/modals/start-reconciliation-modal";
import { TransferStockModal } from "@/components/modals/transfer-stock-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import { DisplaySelect } from "@/components/ui/display-select";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchInput } from "@/components/ui/updated-search-input";
import { InventoryTable } from "@/features/inventory/_components/inventory-table";
import { MovementHistoryDrawer } from "@/features/inventory/_components/movement-history-drawer";
import { PurchaseOrderDetailDrawer } from "@/features/purchase-orders/_components/purchase-order-detail-drawer";
import { PurchaseOrdersTable } from "@/features/purchase-orders/_components/purchase-orders-table";
import { usePurchaseOrdersData } from "@/features/purchase-orders/hooks/use-purchase-orders-data";
import { ReconciliationDetailDrawer } from "@/features/reconciliations/_components/reconciliation-detail-drawer";
import { ReconciliationsTable } from "@/features/reconciliations/_components/reconciliations-table";
import { useReconciliationsData } from "@/features/reconciliations/hooks/use-reconciliations-data";
import { RestockDetailDrawer } from "@/features/restocks/_components/restock-detail-drawer";
import { RestocksTable } from "@/features/restocks/_components/restocks-table";
import { useRestocksData } from "@/features/restocks/hooks/use-restocks-data";
import { SupplierOrderDetailDrawer } from "@/features/supplier-orders/_components/supplier-order-detail-drawer";
import { SupplierOrdersTable } from "@/features/supplier-orders/_components/supplier-orders-table";
import { useSupplierOrdersData } from "@/features/supplier-orders/hooks/use-supplier-orders-data";
import { TransferOrderDetailDrawer } from "@/features/transfer-orders/_components/transfer-order-detail-drawer";
import { TransferOrdersTable } from "@/features/transfer-orders/_components/transfer-orders-table";
import { useTransferOrdersData } from "@/features/transfer-orders/hooks/use-transfer-orders-data";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import {
	getPurchaseOrderDirectionOptions,
	getPurchaseOrderStatusOptions,
} from "@/lib/constants/purchase-order-status";
import { getReconciliationStatusOptions } from "@/lib/constants/reconciliation-status";
import { getRestockStatusOptions } from "@/lib/constants/restock-status";
import { getSupplierOrderStatusOptions } from "@/lib/constants/supplier-order-status";
import {
	getTransferDirectionOptions,
	getTransferOrderStatusOptions,
} from "@/lib/constants/transfer-order-status";
import { toSatangs } from "@/lib/currency";
import { handleConvexError } from "@/lib/errors";
import type {
	CreatePurchaseOrderFormInput,
	PurchaseOrderItemFormInput,
	RejectPurchaseOrderFormInput,
} from "@/lib/schemas/purchase-order";
import type {
	RecordCountFormInput,
	RejectReconciliationFormInput,
	StartReconciliationFormInput,
} from "@/lib/schemas/reconciliation";
import type {
	CreateRestockFormInput,
	RestockItemFormInput,
} from "@/lib/schemas/restock";
import type {
	AdjustStockFormInput,
	TransferStockFormInput,
} from "@/lib/schemas/stock";
import type {
	CreateSupplierOrderFormInput,
	SupplierOrderItemFormInput,
} from "@/lib/schemas/supplier-order";
import type {
	CreateTransferOrderFormInput,
	TransferOrderItemFormInput,
} from "@/lib/schemas/transfer-order";
import { toast } from "@/lib/toast";
import type {
	SupplierPurchaseOrder,
	SupplierPurchaseOrderDirection,
	SupplierPurchaseOrderStatus,
} from "@/lib/types/purchase-order";
import type {
	Reconciliation,
	ReconciliationItem,
	ReconciliationStatus,
} from "@/lib/types/reconciliation";
import type { Restock, RestockItem, RestockStatus } from "@/lib/types/restock";
import type { Room } from "@/lib/types/room";
import type { Stock, StockMovement } from "@/lib/types/stock";
import type {
	SupplierOrder,
	SupplierOrderItem,
	SupplierOrderStatus,
} from "@/lib/types/supplier-order";
import type {
	TransferDirection,
	TransferOrder,
	TransferOrderItem,
	TransferOrderStatus,
} from "@/lib/types/transfer-order";

type InventoryTab =
	| "stock"
	| "restocks"
	| "reconciliations"
	| "transfers"
	| "purchaseOrders"
	| "supplierOrders";

interface InventorySearch {
	branch?: string;
	room?: string;
	tab?: InventoryTab;
	status?: RestockStatus;
	reconStatus?: ReconciliationStatus;
	transferStatus?: TransferOrderStatus;
	transferDirection?: TransferDirection;
	poStatus?: SupplierPurchaseOrderStatus;
	poDirection?: SupplierPurchaseOrderDirection;
	soStatus?: SupplierOrderStatus;
}

export const Route = createFileRoute("/(dashboard)/branch/inventory")({
	component: InventoryPage,
	validateSearch: (search: Record<string, unknown>): InventorySearch => ({
		branch: search.branch as string | undefined,
		room: search.room as string | undefined,
		tab: (search.tab as InventoryTab) || "stock",
		status: search.status as RestockStatus | undefined,
		reconStatus: search.reconStatus as ReconciliationStatus | undefined,
		transferStatus: search.transferStatus as TransferOrderStatus | undefined,
		transferDirection: (search.transferDirection as TransferDirection) || "all",
		poStatus: search.poStatus as SupplierPurchaseOrderStatus | undefined,
		poDirection:
			(search.poDirection as SupplierPurchaseOrderDirection) || "all",
		soStatus: search.soStatus as SupplierOrderStatus | undefined,
	}),
});

const PAGE_SIZE = 20;

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	sortKey: "name",
	columnVisibility: {
		room: true,
		quantity: true,
		unitCost: true,
		estValue: true,
		expiry: true,
		status: true,
	},
};

function InventoryPage() {
	const navigate = useNavigate();
	const {
		branch: searchBranchId,
		room: roomFilter,
		tab,
		status: statusFilter,
		reconStatus: reconStatusFilter,
		transferStatus: transferStatusFilter,
		transferDirection: transferDirectionFilter,
		poStatus: poStatusFilter,
		poDirection: poDirectionFilter,
		soStatus: soStatusFilter,
	} = useSearch({
		from: "/(dashboard)/branch/inventory",
	});

	// Pagination
	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	// Search state
	const [searchQuery, setSearchQuery] = useState("");

	const {
		settings: displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_inventory_display_settings",
		DEFAULT_SETTINGS,
		() => {
			const defaultMode =
				globalThis.window !== undefined && globalThis.innerWidth < 640
					? "cards"
					: "rows";
			return { ...DEFAULT_SETTINGS, viewMode: defaultMode };
		},
	);

	// Stock Modal/Drawer state
	const [adjustModalOpen, setAdjustModalOpen] = useState(false);
	const [transferModalOpen, setTransferModalOpen] = useState(false);
	const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
	const [selectedStock, setSelectedStock] = useState<Stock | null>(null);

	// Restock Modal/Drawer state
	const [createRestockModalOpen, setCreateRestockModalOpen] = useState(false);
	const [editRestockModalOpen, setEditRestockModalOpen] = useState(false);
	const [restockDetailDrawerOpen, setRestockDetailDrawerOpen] = useState(false);
	const [addItemModalOpen, setAddItemModalOpen] = useState(false);
	const [editItemModalOpen, setEditItemModalOpen] = useState(false);
	const [selectedRestock, setSelectedRestock] = useState<Restock | null>(null);
	const [selectedRestockItem, setSelectedRestockItem] =
		useState<RestockItem | null>(null);

	// Reconciliation Modal/Drawer state
	const [startReconciliationModalOpen, setStartReconciliationModalOpen] =
		useState(false);
	const [reconciliationDetailDrawerOpen, setReconciliationDetailDrawerOpen] =
		useState(false);
	const [recordCountModalOpen, setRecordCountModalOpen] = useState(false);
	const [rejectReconciliationModalOpen, setRejectReconciliationModalOpen] =
		useState(false);
	const [selectedReconciliation, setSelectedReconciliation] =
		useState<Reconciliation | null>(null);
	const [selectedReconciliationItem, setSelectedReconciliationItem] =
		useState<ReconciliationItem | null>(null);

	// Transfer Order Modal/Drawer state
	const [createTransferModalOpen, setCreateTransferModalOpen] = useState(false);
	const [transferDetailDrawerOpen, setTransferDetailDrawerOpen] =
		useState(false);
	const [addTransferItemModalOpen, setAddTransferItemModalOpen] =
		useState(false);
	const [selectedTransfer, setSelectedTransfer] =
		useState<TransferOrder | null>(null);
	const [selectedDestinationBranchId, setSelectedDestinationBranchId] =
		useState<string>("");

	// Purchase Order Modal/Drawer state
	const [createPurchaseOrderModalOpen, setCreatePurchaseOrderModalOpen] =
		useState(false);
	const [purchaseOrderDetailDrawerOpen, setPurchaseOrderDetailDrawerOpen] =
		useState(false);
	const [addPurchaseOrderItemModalOpen, setAddPurchaseOrderItemModalOpen] =
		useState(false);
	const [rejectPurchaseOrderModalOpen, setRejectPurchaseOrderModalOpen] =
		useState(false);
	const [selectedPurchaseOrder, setSelectedPurchaseOrder] =
		useState<SupplierOrder | null>(null);

	// Supplier Order Modal/Drawer state
	const [createSupplierOrderModalOpen, setCreateSupplierOrderModalOpen] =
		useState(false);
	const [supplierOrderDetailDrawerOpen, setSupplierOrderDetailDrawerOpen] =
		useState(false);
	const [addSupplierOrderItemModalOpen, setAddSupplierOrderItemModalOpen] =
		useState(false);
	const [editSupplierOrderItemModalOpen, setEditSupplierOrderItemModalOpen] =
		useState(false);
	const [selectedSupplierOrder, setSelectedSupplierOrder] =
		useState<SupplierOrder | null>(null);
	const [selectedSupplierOrderItem, setSelectedSupplierOrderItem] =
		useState<SupplierOrderItem | null>(null);
	const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);

	// Loading states
	const [isAdjusting, setIsAdjusting] = useState(false);
	const [isTransferring, setIsTransferring] = useState(false);
	const [isCreatingRestock, setIsCreatingRestock] = useState(false);
	const [isEditingRestock, setIsEditingRestock] = useState(false);
	const [isAddingItem, setIsAddingItem] = useState(false);
	const [isEditingItem, setIsEditingItem] = useState(false);
	const [isCompletingRestock, setIsCompletingRestock] = useState(false);
	const [isCancellingRestock, setIsCancellingRestock] = useState(false);
	const [isDeletingRestock, setIsDeletingRestock] = useState(false);
	const [isStartingReconciliation, setIsStartingReconciliation] =
		useState(false);
	const [isRecordingCount, setIsRecordingCount] = useState(false);
	const [isSubmittingReconciliation, setIsSubmittingReconciliation] =
		useState(false);
	const [isApprovingReconciliation, setIsApprovingReconciliation] =
		useState(false);
	const [isRejectingReconciliation, setIsRejectingReconciliation] =
		useState(false);

	// Transfer loading states
	const [isCreatingTransfer, setIsCreatingTransfer] = useState(false);
	const [isAddingTransferItem, setIsAddingTransferItem] = useState(false);
	const [isApprovingTransfer, setIsApprovingTransfer] = useState(false);
	const [isShippingTransfer, setIsShippingTransfer] = useState(false);
	const [isReceivingTransfer, setIsReceivingTransfer] = useState(false);
	const [isCompletingTransfer, setIsCompletingTransfer] = useState(false);
	const [isCancellingTransfer, setIsCancellingTransfer] = useState(false);
	const [isDeletingTransfer, setIsDeletingTransfer] = useState(false);

	// Purchase order loading states
	const [isCreatingPurchaseOrder, setIsCreatingPurchaseOrder] = useState(false);
	const [isAddingPurchaseOrderItem, setIsAddingPurchaseOrderItem] =
		useState(false);
	const [isSubmittingPurchaseOrder, setIsSubmittingPurchaseOrder] =
		useState(false);
	const [isApprovingPurchaseOrder, setIsApprovingPurchaseOrder] =
		useState(false);
	const [isRejectingPurchaseOrder, setIsRejectingPurchaseOrder] =
		useState(false);
	const [isMarkingFulfilling, setIsMarkingFulfilling] = useState(false);
	const [isMarkingFulfilled, setIsMarkingFulfilled] = useState(false);
	const [isCancellingPurchaseOrder, setIsCancellingPurchaseOrder] =
		useState(false);
	const [isDeletingPurchaseOrder, setIsDeletingPurchaseOrder] = useState(false);

	// Supplier order loading states
	const [isCreatingSupplierOrder, setIsCreatingSupplierOrder] = useState(false);
	const [isAddingSupplierOrderItem, setIsAddingSupplierOrderItem] =
		useState(false);
	const [isEditingSupplierOrderItem, setIsEditingSupplierOrderItem] =
		useState(false);
	const [isSubmittingSupplierOrder, setIsSubmittingSupplierOrder] =
		useState(false);
	const [isConfirmingSupplierOrder, setIsConfirmingSupplierOrder] =
		useState(false);
	const [isReceivingSupplierOrder, setIsReceivingSupplierOrder] =
		useState(false);
	const [isCancellingSupplierOrder, setIsCancellingSupplierOrder] =
		useState(false);
	const [isDeletingSupplierOrder, setIsDeletingSupplierOrder] = useState(false);

	// Get current user for org context
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	// Query stocks for selected branch
	const stocksData = useQuery(
		api.features.stocks.web.list,
		tab === "stock" && orgId && branchId
			? {
					orgId,
					branchId: branchId as Id<"branches">,
					roomId: roomFilter ? (roomFilter as Id<"rooms">) : undefined,
				}
			: "skip",
	);

	// Query rooms for transfer targets and filter
	const roomsData = useQuery(
		api.features.rooms.web.list,
		orgId && branchId
			? { orgId, branchId: branchId as Id<"branches"> }
			: "skip",
	);

	// Query stock movements for selected stock (for history drawer)
	const movementsData = useQuery(
		api.features.stockMovements.web.getByStock,
		selectedStock && historyDrawerOpen
			? { branchId: branchId as Id<"branches">, stockId: selectedStock._id }
			: "skip",
	);

	const {
		restocksData,
		restockItemsData,
		createRestock,
		updateRestock,
		addRestockItem,
		updateRestockItem,
		removeRestockItem,
		completeRestock,
		cancelRestock,
		deleteRestock,
	} = useRestocksData({
		tab,
		orgId,
		branchId,
		statusFilter,
		selectedRestock,
		restockDetailDrawerOpen,
	});

	// Query suppliers for restock forms
	const suppliersData = useQuery(
		api.features.suppliers.web.list,
		orgId ? { orgId } : "skip",
	);

	const productsWithStockData = useQuery(
		api.features.products.web.getProductsWithStock,
		orgId && branchId
			? {
					orgId,
					branchId: branchId as Id<"branches">,
				}
			: "skip",
	);

	const {
		reconciliationsData,
		reconciliationItemsData,
		startReconciliation,
		recordCount,
		submitReconciliation,
		approveReconciliation,
		rejectReconciliation,
	} = useReconciliationsData({
		tab,
		orgId,
		branchId,
		statusFilter: reconStatusFilter,
		selectedReconciliation,
		reconciliationDetailDrawerOpen,
	});

	// Query transfer orders for selected branch
	const {
		transferOrdersData,
		transferItemsData,
		sourceRoomStocksData,
		destinationRoomsData,
		createTransferOrder,
		addTransferItem,
		removeTransferItem,
		approveTransfer,
		shipTransfer,
		receiveTransfer,
		completeTransfer,
		cancelTransfer,
		deleteTransfer,
	} = useTransferOrdersData({
		tab,
		orgId,
		branchId,
		statusFilter: transferStatusFilter,
		directionFilter: transferDirectionFilter,
		selectedTransfer,
		transferDetailDrawerOpen,
		selectedDestinationBranchId,
	});

	const {
		purchaseOrdersQueryResult,
		purchaseOrderItemsData,
		createPurchaseOrder,
		addPurchaseOrderItem,
		removePurchaseOrderItem,
		submitPurchaseOrder,
		approvePurchaseOrder,
		rejectPurchaseOrder,
		markPurchaseOrderFulfilling,
		markPurchaseOrderFulfilled,
		cancelPurchaseOrder,
		deletePurchaseOrder,
	} = usePurchaseOrdersData({
		tab,
		orgId,
		branchId,
		statusFilter: poStatusFilter,
		selectedPurchaseOrder,
		purchaseOrderDetailDrawerOpen,
	});

	const {
		supplierOrdersQueryResult,
		supplierOrderItemsData,
		createSupplierOrder,
		addSupplierOrderItem,
		updateSupplierOrderItem,
		removeSupplierOrderItem,
		submitSupplierOrder,
		confirmSupplierOrder,
		receiveSupplierOrder,
		cancelSupplierOrder,
		deleteSupplierOrder,
	} = useSupplierOrdersData({
		tab,
		orgId,
		branchId,
		statusFilter: soStatusFilter,
		selectedSupplierOrder,
		supplierOrderDetailDrawerOpen,
	});

	const stocks = (stocksData ?? []) as Stock[];
	const rooms = (roomsData ?? []) as Room[];
	const movements = (movementsData ?? []) as StockMovement[];
	const restocks = (restocksData ?? []) as Restock[];
	const restockItems = (restockItemsData ?? []) as RestockItem[];
	const suppliers = suppliersData ?? [];
	const productsWithStock = productsWithStockData ?? [];
	const products = useMemo(
		() => productsWithStock.map((item) => item.product),
		[productsWithStock],
	);
	const isLoadingStocks =
		isLoadingBranches || (stocksData === undefined && !!branchId);
	const isLoadingMovements = movementsData === undefined && !!selectedStock;
	const isLoadingRestocks =
		isLoadingBranches || (restocksData === undefined && !!branchId);
	const isLoadingRestockItems =
		restockItemsData === undefined && !!selectedRestock;
	const isLoadingProducts = productsWithStockData === undefined && !!branchId;
	const reconciliations = (reconciliationsData ?? []) as Reconciliation[];
	const reconciliationItems = (reconciliationItemsData ??
		[]) as ReconciliationItem[];
	const isLoadingReconciliations =
		reconciliationsData === undefined && !!branchId;
	const isLoadingReconciliationItems =
		reconciliationItemsData === undefined && !!selectedReconciliation;

	// Transfer data
	const transferOrders = (transferOrdersData ??
		[]) as unknown as TransferOrder[];
	const transferItems = transferItemsData ?? [];
	const destinationRooms = destinationRoomsData ?? [];
	const isLoadingTransfers = transferOrdersData === undefined && !!branchId;
	const isLoadingTransferItems =
		transferItemsData === undefined && !!selectedTransfer;

	// Purchase order data
	const purchaseOrders = useMemo(() => {
		return ((purchaseOrdersQueryResult?.page ?? []) as SupplierOrder[]).map(
			(po: SupplierOrder) =>
				({
					...po,
					_id: po._id as unknown as Id<"purchaseOrders">,
					requestingBranchId: po.branchId,
					fulfillingBranchId: po.branchId,
					requestedAt: po.orderDate,
					requestedBy: po.createdBy,
					orderNumber: po.orderNumber,
					totalItems: po.totalItems,
				}) as unknown as SupplierPurchaseOrder,
		);
	}, [purchaseOrdersQueryResult]);

	const purchaseOrderItems = (purchaseOrderItemsData ??
		[]) as SupplierOrderItem[];
	const isLoadingPurchaseOrders =
		purchaseOrdersQueryResult === undefined && !!branchId;
	const isLoadingPurchaseOrderItems =
		purchaseOrderItemsData === undefined && !!selectedPurchaseOrder;

	// Supplier order data
	const supplierOrders = (supplierOrdersQueryResult?.page ??
		[]) as SupplierOrder[];
	const supplierOrderItems = (supplierOrderItemsData ??
		[]) as SupplierOrderItem[];
	const isLoadingSupplierOrders =
		supplierOrdersQueryResult === undefined && !!branchId;
	const isLoadingSupplierOrderItems =
		supplierOrderItemsData === undefined && !!selectedSupplierOrder;

	// Mutations
	const adjustStock = useMutation(api.features.stocks.web.adjust);
	const transferStock = useMutation(api.features.stocks.web.transferRoom);

	// Filter stocks based on search
	const filteredStocks = useMemo(() => {
		if (!searchQuery) return stocks;

		const query = searchQuery.toLowerCase();
		return stocks.filter(
			(s) =>
				s.productName?.toLowerCase().includes(query) ||
				s.productSku?.toLowerCase().includes(query) ||
				s.roomName?.toLowerCase().includes(query),
		);
	}, [stocks, searchQuery]);

	// Apply sorting
	const sortedStocks = useMemo(() => {
		return [...filteredStocks].sort((a, b) => {
			if (displaySettings.sortKey === "name") {
				return (a.productName || "").localeCompare(b.productName || "");
			}
			if (displaySettings.sortKey === "quantity") {
				return b.quantity - a.quantity;
			}
			if (displaySettings.sortKey === "value") {
				const valA = a.quantity * a.unitCostSatangs;
				const valB = b.quantity * b.unitCostSatangs;
				return valB - valA;
			}
			if (displaySettings.sortKey === "expiry") {
				if (!a.expiryDate && !b.expiryDate) return 0;
				if (!a.expiryDate) return 1;
				if (!b.expiryDate) return -1;
				return a.expiryDate - b.expiryDate;
			}
			return 0;
		});
	}, [filteredStocks, displaySettings.sortKey]);

	// Paginate stocks (client-side)
	const paginatedStocks = sortedStocks.slice(
		pagination.pageIndex * pagination.pageSize,
		(pagination.pageIndex + 1) * pagination.pageSize,
	);

	// Handle branch selection
	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			const newBranchId = Array.isArray(value) ? value.at(-1) : value;
			navigate({
				to: ".",
				search: (prev) => ({ ...prev, branch: newBranchId }),
			});
		},
		[navigate],
	);

	// Handle tab change
	const handleTabChange = useCallback(
		(newTab: string) => {
			navigate({
				to: ".",
				search: (prev) => ({ ...prev, tab: newTab }),
			});
		},
		[navigate],
	);

	// Handle status filter change (restocks)
	const handleStatusChange = useCallback(
		(newStatus: string | null) => {
			if (!newStatus) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						status: newStatus === "all" ? undefined : newStatus,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle reconciliation status filter change
	const handleReconStatusChange = useCallback(
		(newStatus: string | null) => {
			if (!newStatus) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						reconStatus: newStatus === "all" ? undefined : newStatus,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle transfer status filter change
	const handleTransferStatusChange = useCallback(
		(newStatus: string | null) => {
			if (!newStatus) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						transferStatus: newStatus === "all" ? undefined : newStatus,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle transfer direction filter change
	const handleTransferDirectionChange = useCallback(
		(newDirection: string | null) => {
			if (!newDirection) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						transferDirection: newDirection,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle purchase order status filter change
	const handlePOStatusChange = useCallback(
		(newStatus: string | null) => {
			if (!newStatus) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						poStatus: newStatus === "all" ? undefined : newStatus,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle purchase order direction filter change
	const handlePODirectionChange = useCallback(
		(newDirection: string | null) => {
			if (!newDirection) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						poDirection: newDirection,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle supplier order status filter change
	const handleSOStatusChange = useCallback(
		(newStatus: string | null) => {
			if (!newStatus) return;
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						soStatus: newStatus === "all" ? undefined : newStatus,
					};
				},
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handle search
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value);
			resetPage();
		},
		[resetPage],
	);

	// Stock Handlers
	const handleAdjust = useCallback((stock: Stock) => {
		setSelectedStock(stock);
		setAdjustModalOpen(true);
	}, []);

	const handleTransfer = useCallback((stock: Stock) => {
		setSelectedStock(stock);
		setTransferModalOpen(true);
	}, []);

	const handleViewHistory = useCallback((stock: Stock) => {
		setSelectedStock(stock);
		setHistoryDrawerOpen(true);
	}, []);

	const handleAdjustSubmit = useCallback(
		async (data: AdjustStockFormInput) => {
			if (!selectedStock || !branchId) return;

			setIsAdjusting(true);
			try {
				await adjustStock({
					branchId: branchId as Id<"branches">,
					stockId: selectedStock._id,
					quantityChange: data.quantityChange,
					reason: data.reason,
				});
				toast.success(<>Stock adjusted</>);
			} catch (error) {
				handleConvexError(error, "Failed to adjust stock");
				throw error;
			} finally {
				setIsAdjusting(false);
			}
		},
		[selectedStock, adjustStock, branchId],
	);

	const handleTransferSubmit = useCallback(
		async (data: TransferStockFormInput) => {
			if (!selectedStock || !branchId) return;

			setIsTransferring(true);
			try {
				await transferStock({
					branchId: branchId as Id<"branches">,
					stockId: selectedStock._id,
					targetRoomId: data.targetRoomId as Id<"rooms">,
				});
				toast.success(<>Stock transferred</>);
				resetPage();
			} catch (error) {
				handleConvexError(error, "Failed to transfer stock");
				throw error;
			} finally {
				setIsTransferring(false);
			}
		},
		[selectedStock, transferStock, branchId, resetPage],
	);

	// Restock Handlers
	const handleViewRestock = useCallback((restock: Restock) => {
		setSelectedRestock(restock);
		setRestockDetailDrawerOpen(true);
	}, []);

	const handleEditRestock = useCallback((restock: Restock) => {
		setSelectedRestock(restock);
		setEditRestockModalOpen(true);
	}, []);

	const handleCreateRestockSubmit = useCallback(
		async (data: CreateRestockFormInput) => {
			if (!branchId || !orgId) return;

			setIsCreatingRestock(true);
			try {
				await createRestock({
					orgId,
					branchId: branchId as Id<"branches">,
					supplierId: data.supplierId
						? (data.supplierId as Id<"suppliers">)
						: undefined,
					supplierInvoiceNumber: data.supplierInvoiceNumber,
					notes: data.notes,
				});
				toast.success(<>Restock created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create restock");
				throw error;
			} finally {
				setIsCreatingRestock(false);
			}
		},
		[branchId, orgId, createRestock],
	);

	const handleEditRestockSubmit = useCallback(
		async (data: CreateRestockFormInput) => {
			if (!selectedRestock || !orgId || !branchId) return;

			setIsEditingRestock(true);
			try {
				await updateRestock({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: selectedRestock._id,
					supplierId: data.supplierId
						? (data.supplierId as Id<"suppliers">)
						: undefined,
					supplierInvoiceNumber: data.supplierInvoiceNumber,
					notes: data.notes,
				});
				toast.success(<>Restock updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update restock");
				throw error;
			} finally {
				setIsEditingRestock(false);
			}
		},
		[selectedRestock, orgId, updateRestock, branchId],
	);

	const handleAddRestockItem = useCallback(() => {
		setAddItemModalOpen(true);
	}, []);

	const handleEditRestockItem = useCallback((item: RestockItem) => {
		setSelectedRestockItem(item);
		setEditItemModalOpen(true);
	}, []);

	const handleAddItemSubmit = useCallback(
		async (data: RestockItemFormInput) => {
			if (!selectedRestock || !branchId || !orgId) return;

			setIsAddingItem(true);
			try {
				await addRestockItem({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: selectedRestock._id,
					productId: data.productId as Id<"products">,
					roomId: data.roomId as Id<"rooms">,
					expectedQuantity: data.expectedQuantity,
					unitCostSatangs: toSatangs(data.unitCostBaht),
					batchNumber: data.batchNumber,
					lotNumber: data.lotNumber,
					expiryDate: data.expiryDate?.getTime(),
				});
				toast.success(<>Item added</>);
			} catch (error) {
				handleConvexError(error, "Failed to add item");
				throw error;
			} finally {
				setIsAddingItem(false);
			}
		},
		[selectedRestock, branchId, orgId, addRestockItem],
	);

	const handleEditItemSubmit = useCallback(
		async (data: RestockItemFormInput) => {
			if (!selectedRestock || !selectedRestockItem || !branchId || !orgId)
				return;

			setIsEditingItem(true);
			try {
				await updateRestockItem({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: selectedRestock._id,
					itemId: selectedRestockItem._id,
					expectedQuantity: data.expectedQuantity,
					unitCostSatangs: toSatangs(data.unitCostBaht),
					batchNumber: data.batchNumber,
					lotNumber: data.lotNumber,
					expiryDate: data.expiryDate?.getTime(),
				});
				toast.success(<>Item updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update item");
				throw error;
			} finally {
				setIsEditingItem(false);
			}
		},
		[selectedRestock, selectedRestockItem, branchId, orgId, updateRestockItem],
	);

	const handleRemoveRestockItem = useCallback(
		async (item: RestockItem) => {
			if (!selectedRestock || !branchId || !orgId) return;

			try {
				await removeRestockItem({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: selectedRestock._id,
					itemId: item._id,
				});
				toast.success(<>Item removed</>);
			} catch (error) {
				handleConvexError(error, "Failed to remove item");
			}
		},
		[selectedRestock, branchId, orgId, removeRestockItem],
	);

	const handleCompleteRestock = useCallback(
		async (restock: Restock) => {
			if (!branchId || !orgId) return;

			setIsCompletingRestock(true);
			try {
				await completeRestock({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: restock._id,
				});
				toast.success(<>Restock completed</>);
				setRestockDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to complete restock");
			} finally {
				setIsCompletingRestock(false);
			}
		},
		[branchId, orgId, completeRestock],
	);

	const handleCancelRestock = useCallback(
		async (restock: Restock) => {
			if (!branchId || !orgId) return;

			setIsCancellingRestock(true);
			try {
				await cancelRestock({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: restock._id,
				});
				toast.success(<>Restock cancelled</>);
				setRestockDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to cancel restock");
			} finally {
				setIsCancellingRestock(false);
			}
		},
		[branchId, orgId, cancelRestock],
	);

	const handleDeleteRestock = useCallback(
		async (restock: Restock) => {
			if (!branchId || !orgId) return;

			setIsDeletingRestock(true);
			try {
				await deleteRestock({
					orgId,
					branchId: branchId as Id<"branches">,
					restockId: restock._id,
				});
				toast.success(<>Restock deleted</>);
				setRestockDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to delete restock");
			} finally {
				setIsDeletingRestock(false);
			}
		},
		[branchId, orgId, deleteRestock],
	);

	// Reconciliation Handlers
	const handleViewReconciliation = useCallback((recon: Reconciliation) => {
		setSelectedReconciliation(recon);
		setReconciliationDetailDrawerOpen(true);
	}, []);

	const handleContinueReconciliation = useCallback((recon: Reconciliation) => {
		setSelectedReconciliation(recon);
		setReconciliationDetailDrawerOpen(true);
	}, []);

	const handleStartReconciliationSubmit = useCallback(
		async (data: StartReconciliationFormInput) => {
			if (!branchId || !orgId) return;

			setIsStartingReconciliation(true);
			try {
				await startReconciliation({
					orgId,
					branchId: branchId as Id<"branches">,
					roomId: data.roomId ? (data.roomId as Id<"rooms">) : undefined,
					notes: data.notes,
				});
				toast.success(<>Reconciliation started</>);
			} catch (error) {
				handleConvexError(error, "Failed to start reconciliation");
				throw error;
			} finally {
				setIsStartingReconciliation(false);
			}
		},
		[branchId, orgId, startReconciliation],
	);

	const handleRecordCount = useCallback((item: ReconciliationItem) => {
		setSelectedReconciliationItem(item);
		setRecordCountModalOpen(true);
	}, []);

	const handleRecordCountSubmit = useCallback(
		async (data: RecordCountFormInput) => {
			if (
				!selectedReconciliation ||
				!selectedReconciliationItem ||
				!orgId ||
				!branchId
			)
				return;

			setIsRecordingCount(true);
			try {
				await recordCount({
					orgId,
					reconciliationId: selectedReconciliation._id,
					itemId: selectedReconciliationItem._id,
					countedQuantity: data.countedQuantity,
					varianceReason: data.varianceReason,
				});
				toast.success(<>Count recorded</>);
			} catch (error) {
				handleConvexError(error, "Failed to record count");
				throw error;
			} finally {
				setIsRecordingCount(false);
			}
		},
		[
			selectedReconciliation,
			selectedReconciliationItem,
			orgId,
			recordCount,
			branchId,
		],
	);

	const handleSubmitReconciliation = useCallback(
		async (recon: Reconciliation) => {
			if (!orgId || !branchId) return;

			setIsSubmittingReconciliation(true);
			try {
				await submitReconciliation({
					orgId,
					reconciliationId: recon._id,
				});
				toast.success(<>Reconciliation submitted for review</>);
			} catch (error) {
				handleConvexError(error, "Failed to submit reconciliation");
			} finally {
				setIsSubmittingReconciliation(false);
			}
		},
		[orgId, submitReconciliation, branchId],
	);

	const handleApproveReconciliation = useCallback(
		async (recon: Reconciliation) => {
			if (!orgId || !branchId) return;

			setIsApprovingReconciliation(true);
			try {
				await approveReconciliation({
					orgId,
					reconciliationId: recon._id,
				});
				toast.success(<>Reconciliation approved</>);
				setReconciliationDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to approve reconciliation");
			} finally {
				setIsApprovingReconciliation(false);
			}
		},
		[orgId, approveReconciliation, branchId],
	);

	const handleRejectReconciliation = useCallback((recon: Reconciliation) => {
		setSelectedReconciliation(recon);
		setRejectReconciliationModalOpen(true);
	}, []);

	const handleRejectReconciliationSubmit = useCallback(
		async (data: RejectReconciliationFormInput) => {
			if (!selectedReconciliation || !orgId || !branchId) return;

			setIsRejectingReconciliation(true);
			try {
				await rejectReconciliation({
					orgId,
					reconciliationId: selectedReconciliation._id,
					notes: data.notes,
				});
				toast.success(<>Reconciliation rejected</>);
				setReconciliationDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to reject reconciliation");
				throw error;
			} finally {
				setIsRejectingReconciliation(false);
			}
		},
		[selectedReconciliation, orgId, rejectReconciliation, branchId],
	);

	// Transfer Order Handlers
	const handleViewTransfer = useCallback((transfer: TransferOrder) => {
		setSelectedTransfer(transfer);
		setTransferDetailDrawerOpen(true);
	}, []);

	const handleCreateTransferSubmit = useCallback(
		async (data: CreateTransferOrderFormInput) => {
			if (!branchId || !orgId) return;

			setIsCreatingTransfer(true);
			try {
				await createTransferOrder({
					orgId,
					sourceBranchId: branchId as Id<"branches">,
					sourceRoomId: data.sourceRoomId as Id<"rooms">,
					destinationBranchId: data.destinationBranchId as Id<"branches">,
					destinationRoomId: data.destinationRoomId
						? (data.destinationRoomId as Id<"rooms">)
						: undefined,
					reason: data.reason,
					notes: data.notes,
				});
				toast.success(<>Transfer order created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create transfer order");
				throw error;
			} finally {
				setIsCreatingTransfer(false);
			}
		},
		[branchId, orgId, createTransferOrder],
	);

	const handleAddTransferItem = useCallback(() => {
		setAddTransferItemModalOpen(true);
	}, []);

	const handleAddTransferItemSubmit = useCallback(
		async (data: TransferOrderItemFormInput) => {
			if (!selectedTransfer || !orgId || !branchId) return;

			setIsAddingTransferItem(true);
			try {
				await addTransferItem({
					orgId,
					transferOrderId: selectedTransfer._id,
					productId: data.productId as Id<"products">,
					stockId: data.stockId as Id<"stocks">,
					quantity: data.quantity,
				});
				toast.success(<>Item added</>);
			} catch (error) {
				handleConvexError(error, "Failed to add item");
				throw error;
			} finally {
				setIsAddingTransferItem(false);
			}
		},
		[selectedTransfer, orgId, addTransferItem, branchId],
	);

	const handleRemoveTransferItem = useCallback(
		async (item: TransferOrderItem) => {
			if (!selectedTransfer || !orgId || !branchId) return;

			try {
				await removeTransferItem({
					orgId,
					transferOrderId: selectedTransfer._id,
					itemId: item._id,
				});
				toast.success(<>Item removed</>);
			} catch (error) {
				handleConvexError(error, "Failed to remove item");
			}
		},
		[selectedTransfer, orgId, removeTransferItem, branchId],
	);

	const handleApproveTransfer = useCallback(
		async (transfer: TransferOrder) => {
			if (!orgId || !branchId) return;

			setIsApprovingTransfer(true);
			try {
				await approveTransfer({
					orgId,
					transferOrderId: transfer._id,
				});
				toast.success(<>Transfer approved</>);
			} catch (error) {
				handleConvexError(error, "Failed to approve transfer");
			} finally {
				setIsApprovingTransfer(false);
			}
		},
		[orgId, approveTransfer, branchId],
	);

	const handleShipTransfer = useCallback(
		async (transfer: TransferOrder) => {
			if (!orgId || !branchId) return;

			setIsShippingTransfer(true);
			try {
				await shipTransfer({
					orgId,
					transferOrderId: transfer._id,
				});
				toast.success(<>Transfer marked as shipped</>);
			} catch (error) {
				handleConvexError(error, "Failed to ship transfer");
			} finally {
				setIsShippingTransfer(false);
			}
		},
		[orgId, shipTransfer, branchId],
	);

	const handleReceiveTransfer = useCallback(
		async (transfer: TransferOrder) => {
			if (!orgId || !branchId) return;

			setIsReceivingTransfer(true);
			try {
				await receiveTransfer({
					orgId,
					transferOrderId: transfer._id,
				});
				toast.success(<>Transfer marked as received</>);
			} catch (error) {
				handleConvexError(error, "Failed to receive transfer");
			} finally {
				setIsReceivingTransfer(false);
			}
		},
		[orgId, receiveTransfer, branchId],
	);

	const handleCompleteTransfer = useCallback(
		async (transfer: TransferOrder) => {
			if (!orgId || !branchId) return;

			setIsCompletingTransfer(true);
			try {
				await completeTransfer({
					orgId,
					transferOrderId: transfer._id,
				});
				toast.success(<>Transfer completed</>);
				setTransferDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to complete transfer");
			} finally {
				setIsCompletingTransfer(false);
			}
		},
		[orgId, completeTransfer, branchId],
	);

	const handleCancelTransfer = useCallback(
		async (transfer: TransferOrder) => {
			if (!orgId || !branchId) return;

			setIsCancellingTransfer(true);
			try {
				await cancelTransfer({
					orgId,
					transferOrderId: transfer._id,
				});
				toast.success(<>Transfer cancelled</>);
				setTransferDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to cancel transfer");
			} finally {
				setIsCancellingTransfer(false);
			}
		},
		[orgId, cancelTransfer, branchId],
	);

	const handleDeleteTransfer = useCallback(
		async (transfer: TransferOrder) => {
			if (!orgId || !branchId) return;

			setIsDeletingTransfer(true);
			try {
				await deleteTransfer({
					orgId,
					transferOrderId: transfer._id,
				});
				toast.success(<>Transfer deleted</>);
				setTransferDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to delete transfer");
			} finally {
				setIsDeletingTransfer(false);
			}
		},
		[orgId, deleteTransfer, branchId],
	);

	// Purchase Order Handlers
	const handleViewPurchaseOrder = useCallback(
		(po: SupplierPurchaseOrder) => {
			const original = (purchaseOrdersQueryResult?.page ?? []).find(
				(o) => (o._id as string) === (po._id as string),
			);
			if (original) {
				setSelectedPurchaseOrder(original);
				setPurchaseOrderDetailDrawerOpen(true);
			}
		},
		[purchaseOrdersQueryResult?.page],
	);

	const handleCreatePurchaseOrderSubmit = useCallback(
		async (data: CreatePurchaseOrderFormInput) => {
			if (!branchId || !orgId) return;

			setIsCreatingPurchaseOrder(true);
			try {
				await createPurchaseOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					supplierId: data.supplierId as Id<"suppliers">,
					notes: data.notes,
				});
				toast.success(<>Purchase order created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create purchase order");
				throw error;
			} finally {
				setIsCreatingPurchaseOrder(false);
			}
		},
		[branchId, orgId, createPurchaseOrder],
	);

	const handleAddPurchaseOrderItem = useCallback(() => {
		setAddPurchaseOrderItemModalOpen(true);
	}, []);

	const handleAddPurchaseOrderItemSubmit = useCallback(
		async (data: PurchaseOrderItemFormInput) => {
			if (!selectedPurchaseOrder || !orgId || !branchId) return;

			setIsAddingPurchaseOrderItem(true);
			try {
				await addPurchaseOrderItem({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: selectedPurchaseOrder._id,
					productId: data.productId as Id<"products">,
					quantity: data.requestedQuantity, // Renamed from requestedQuantity
					unitCostSatangs: 0, // Default for inter-branch, might need schema update
				});
				toast.success(<>Item added</>);
			} catch (error) {
				handleConvexError(error, "Failed to add item");
				throw error;
			} finally {
				setIsAddingPurchaseOrderItem(false);
			}
		},
		[selectedPurchaseOrder, orgId, addPurchaseOrderItem, branchId],
	);

	const handleRemovePurchaseOrderItem = useCallback(
		async (item: SupplierOrderItem) => {
			if (!selectedPurchaseOrder || !orgId || !branchId) return;

			try {
				await removePurchaseOrderItem({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: selectedPurchaseOrder._id,
					itemId: item._id,
				});
				toast.success(<>Item removed</>);
			} catch (error) {
				handleConvexError(error, "Failed to remove item");
			}
		},
		[selectedPurchaseOrder, orgId, removePurchaseOrderItem, branchId],
	);

	const handleSubmitPurchaseOrder = useCallback(
		async (po: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsSubmittingPurchaseOrder(true);
			try {
				await submitPurchaseOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: po._id,
				});
				toast.success(<>Purchase order submitted for approval</>);
			} catch (error) {
				handleConvexError(error, "Failed to submit purchase order");
			} finally {
				setIsSubmittingPurchaseOrder(false);
			}
		},
		[orgId, submitPurchaseOrder, branchId],
	);

	const handleApprovePurchaseOrder = useCallback(
		async (po: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsApprovingPurchaseOrder(true);
			try {
				await approvePurchaseOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: po._id,
				});
				toast.success(<>Purchase order approved</>);
				setPurchaseOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to approve purchase order");
			} finally {
				setIsApprovingPurchaseOrder(false);
			}
		},
		[orgId, approvePurchaseOrder, branchId],
	);

	const handleRejectPurchaseOrder = useCallback((po: SupplierOrder) => {
		setSelectedPurchaseOrder(po);
		setRejectPurchaseOrderModalOpen(true);
	}, []);

	const handleRejectPurchaseOrderSubmit = useCallback(
		async (data: RejectPurchaseOrderFormInput) => {
			if (!selectedPurchaseOrder || !orgId || !branchId) return;

			setIsRejectingPurchaseOrder(true);
			try {
				await rejectPurchaseOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: selectedPurchaseOrder._id,
					reason: data.rejectionReason, // Renamed from rejectionReason
				});
				toast.success(<>Purchase order rejected</>);
				setPurchaseOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to reject purchase order");
				throw error;
			} finally {
				setIsRejectingPurchaseOrder(false);
			}
		},
		[selectedPurchaseOrder, orgId, rejectPurchaseOrder, branchId],
	);

	const handleMarkFulfilling = useCallback(
		async (po: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsMarkingFulfilling(true);
			try {
				await markPurchaseOrderFulfilling({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: po._id,
				});
				toast.success(<>Purchase order marked as fulfilling</>);
			} catch (error) {
				handleConvexError(error, "Failed to update purchase order");
			} finally {
				setIsMarkingFulfilling(false);
			}
		},
		[orgId, markPurchaseOrderFulfilling, branchId],
	);

	const handleMarkFulfilled = useCallback(
		async (po: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsMarkingFulfilled(true);
			try {
				await markPurchaseOrderFulfilled({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: po._id,
				});
				toast.success(<>Purchase order marked as fulfilled</>);
				setPurchaseOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to update purchase order");
			} finally {
				setIsMarkingFulfilled(false);
			}
		},
		[orgId, markPurchaseOrderFulfilled, branchId],
	);

	const handleCancelPurchaseOrder = useCallback(
		async (po: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsCancellingPurchaseOrder(true);
			try {
				await cancelPurchaseOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: po._id,
				});
				toast.success(<>Purchase order cancelled</>);
				setPurchaseOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to cancel purchase order");
			} finally {
				setIsCancellingPurchaseOrder(false);
			}
		},
		[orgId, cancelPurchaseOrder, branchId],
	);

	const handleDeletePurchaseOrder = useCallback(
		async (po: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsDeletingPurchaseOrder(true);
			try {
				await deletePurchaseOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					purchaseOrderId: po._id,
				});
				toast.success(<>Purchase order deleted</>);
				setPurchaseOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to delete purchase order");
			} finally {
				setIsDeletingPurchaseOrder(false);
			}
		},
		[orgId, deletePurchaseOrder, branchId],
	);

	// Supplier Order Handlers
	const handleViewSupplierOrder = useCallback((so: SupplierOrder) => {
		setSelectedSupplierOrder(so);
		setSupplierOrderDetailDrawerOpen(true);
	}, []);

	const handleCreateSupplierOrderSubmit = useCallback(
		async (data: CreateSupplierOrderFormInput) => {
			if (!branchId || !orgId) return;

			setIsCreatingSupplierOrder(true);
			try {
				await createSupplierOrder({
					orgId,
					branchId: branchId as Id<"branches">,
					supplierId: data.supplierId as Id<"suppliers">,
					expectedDeliveryDate: data.expectedDeliveryDate?.getTime(),
					notes: data.notes,
				});
				toast.success(<>Supplier order created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create supplier order");
				throw error;
			} finally {
				setIsCreatingSupplierOrder(false);
			}
		},
		[branchId, orgId, createSupplierOrder],
	);

	const handleAddSupplierOrderItem = useCallback(() => {
		setAddSupplierOrderItemModalOpen(true);
	}, []);

	const handleEditSupplierOrderItem = useCallback((item: SupplierOrderItem) => {
		setSelectedSupplierOrderItem(item);
		setEditSupplierOrderItemModalOpen(true);
	}, []);

	const handleAddSupplierOrderItemSubmit = useCallback(
		async (data: SupplierOrderItemFormInput) => {
			if (!selectedSupplierOrder || !orgId || !branchId) return;

			setIsAddingSupplierOrderItem(true);
			try {
				await addSupplierOrderItem({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: selectedSupplierOrder._id,
					productId: data.productId as Id<"products">,
					quantity: data.quantity,
					unitCostSatangs: toSatangs(data.unitCostBaht),
					cannabisWeightGrams: data.cannabisWeightGrams,
				});
				toast.success(<>Item added</>);
			} catch (error) {
				handleConvexError(error, "Failed to add item");
				throw error;
			} finally {
				setIsAddingSupplierOrderItem(false);
			}
		},
		[selectedSupplierOrder, orgId, addSupplierOrderItem, branchId],
	);

	const handleEditSupplierOrderItemSubmit = useCallback(
		async (data: {
			quantity: number;
			unitCostBaht: number;
			cannabisWeightGrams?: number;
		}) => {
			if (
				!selectedSupplierOrder ||
				!selectedSupplierOrderItem ||
				!orgId ||
				!branchId
			)
				return;

			setIsEditingSupplierOrderItem(true);
			try {
				await updateSupplierOrderItem({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: selectedSupplierOrder._id,
					itemId: selectedSupplierOrderItem._id,
					quantity: data.quantity,
					unitCostSatangs: toSatangs(data.unitCostBaht),
					cannabisWeightGrams: data.cannabisWeightGrams,
				});
				toast.success(<>Item updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update item");
				throw error;
			} finally {
				setIsEditingSupplierOrderItem(false);
			}
		},
		[
			selectedSupplierOrder,
			selectedSupplierOrderItem,
			orgId,
			updateSupplierOrderItem,
			branchId,
		],
	);

	const handleRemoveSupplierOrderItem = useCallback(
		async (item: SupplierOrderItem) => {
			if (!selectedSupplierOrder || !orgId || !branchId) return;

			try {
				await removeSupplierOrderItem({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: selectedSupplierOrder._id,
					itemId: item._id,
				});
				toast.success(<>Item removed</>);
			} catch (error) {
				handleConvexError(error, "Failed to remove item");
			}
		},
		[selectedSupplierOrder, orgId, removeSupplierOrderItem, branchId],
	);

	const handleSubmitSupplierOrder = useCallback(
		async (so: SupplierOrder) => {
			if (!orgId || !branchId) return;

			setIsSubmittingSupplierOrder(true);
			try {
				await submitSupplierOrder({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: so._id,
				});
				toast.success(<>Supplier order submitted</>);
			} catch (error) {
				handleConvexError(error, "Failed to submit supplier order");
			} finally {
				setIsSubmittingSupplierOrder(false);
			}
		},
		[orgId, branchId, submitSupplierOrder],
	);

	const handleConfirmSupplierOrder = useCallback(
		async (so: SupplierOrder) => {
			if (!orgId) return;

			setIsConfirmingSupplierOrder(true);
			try {
				await confirmSupplierOrder({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: so._id,
				});
				toast.success(<>Supplier order confirmed</>);
			} catch (error) {
				handleConvexError(error, "Failed to confirm supplier order");
			} finally {
				setIsConfirmingSupplierOrder(false);
			}
		},
		[orgId, confirmSupplierOrder, branchId],
	);

	const handleReceiveSupplierOrder = useCallback(
		async (so: SupplierOrder) => {
			if (!orgId) return;

			setIsReceivingSupplierOrder(true);
			try {
				const result = await receiveSupplierOrder({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: so._id,
				});
				if (result) {
					toast.success(<>Supplier order received and restock created</>);
				} else {
					toast.success(<>Supplier order received</>);
				}
				setSupplierOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to receive supplier order");
			} finally {
				setIsReceivingSupplierOrder(false);
			}
		},
		[orgId, receiveSupplierOrder, branchId],
	);

	const handleCancelSupplierOrder = useCallback(
		async (so: SupplierOrder) => {
			if (!orgId) return;

			setIsCancellingSupplierOrder(true);
			try {
				await cancelSupplierOrder({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: so._id,
				});
				toast.success(<>Supplier order cancelled</>);
				setSupplierOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to cancel supplier order");
			} finally {
				setIsCancellingSupplierOrder(false);
			}
		},
		[orgId, cancelSupplierOrder, branchId],
	);

	const handleDeleteSupplierOrder = useCallback(
		async (so: SupplierOrder) => {
			if (!orgId) return;

			setIsDeletingSupplierOrder(true);
			try {
				await deleteSupplierOrder({
					branchId: branchId as Id<"branches">,
					orgId,
					purchaseOrderId: so._id,
				});
				toast.success(<>Supplier order deleted</>);
				setSupplierOrderDetailDrawerOpen(false);
			} catch (error) {
				handleConvexError(error, "Failed to delete supplier order");
			} finally {
				setIsDeletingSupplierOrder(false);
			}
		},
		[orgId, deleteSupplierOrder, branchId],
	);

	return (
		<PageContent
			title="Inventory"
			controls={
				<div className="flex items-center gap-3">
					<BranchSelector
						value={branchId as Id<"branches"> | undefined}
						onValueChange={handleBranchChange}
						placeholder="Select branch"
						className="w-48"
						open={branchSelectorOpen}
						onOpenChange={setBranchSelectorOpen}
						onViewAll={() => navigate({ to: "/settings/branches" })}
						onCreateNew={() => navigate({ to: "/settings/branches" })}
					/>
				</div>
			}
		>
			{!branchId && !isLoadingBranches ? (
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No branches found"
						description="Get started by creating your first branch to manage stocks and transfers."
						actionLabel="Create branch"
						onAction={() => navigate({ to: "/settings/branches" })}
						icon={
							<HugeiconsIcon
								icon={Store01Icon}
								className="h-5 w-5 text-neutral-700"
								strokeWidth={2.2}
							/>
						}
					/>
				</div>
			) : (
				<Tabs
					value={tab}
					onValueChange={handleTabChange}
					className="flex flex-1 flex-col"
				>
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<TabsList variant="line">
							<TabsTrigger value="stock">Stock</TabsTrigger>
							<TabsTrigger value="restocks">Restocks</TabsTrigger>
							<TabsTrigger value="reconciliations">Reconciliations</TabsTrigger>
							<TabsTrigger value="transfers">Transfers</TabsTrigger>
							<TabsTrigger value="purchaseOrders">Purchase Orders</TabsTrigger>
							<TabsTrigger value="supplierOrders">Supplier Orders</TabsTrigger>
						</TabsList>

						{tab === "restocks" && (
							<div className="flex items-center gap-3">
								<Select
									value={statusFilter ?? "all"}
									items={[
										{ value: "all", label: "All statuses" },
										...getRestockStatusOptions(),
									]}
									onValueChange={handleStatusChange}
								>
									<SelectTrigger className="w-36">
										<SelectValue placeholder="All statuses" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All statuses</SelectItem>
										{getRestockStatusOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Can scope="restocks.create">
									<ActionHeaderButton
										label="Create restock"
										onClick={() => setCreateRestockModalOpen(true)}
									/>
								</Can>
							</div>
						)}

						{tab === "reconciliations" && (
							<div className="flex items-center gap-3">
								<Select
									value={reconStatusFilter ?? "all"}
									items={[
										{ value: "all", label: "All statuses" },
										...getReconciliationStatusOptions(),
									]}
									onValueChange={handleReconStatusChange}
								>
									<SelectTrigger className="w-40">
										<SelectValue placeholder="All statuses" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All statuses</SelectItem>
										{getReconciliationStatusOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Can scope="reconciliations.create">
									<ActionHeaderButton
										label="Start count"
										onClick={() => setStartReconciliationModalOpen(true)}
									/>
								</Can>
							</div>
						)}

						{tab === "transfers" && (
							<div className="flex items-center gap-3">
								<Select
									value={transferDirectionFilter}
									items={getTransferDirectionOptions()}
									onValueChange={handleTransferDirectionChange}
								>
									<SelectTrigger className="w-36">
										<SelectValue placeholder="All transfers" />
									</SelectTrigger>
									<SelectContent>
										{getTransferDirectionOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Select
									value={transferStatusFilter ?? "all"}
									items={[
										{ value: "all", label: "All statuses" },
										...getTransferOrderStatusOptions(),
									]}
									onValueChange={handleTransferStatusChange}
								>
									<SelectTrigger className="w-36">
										<SelectValue placeholder="All statuses" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All statuses</SelectItem>
										{getTransferOrderStatusOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Can scope="transferOrders.create">
									<ActionHeaderButton
										label="New transfer"
										onClick={() => setCreateTransferModalOpen(true)}
									/>
								</Can>
							</div>
						)}

						{tab === "purchaseOrders" && (
							<div className="flex items-center gap-3">
								<Select
									value={poDirectionFilter}
									items={getPurchaseOrderDirectionOptions()}
									onValueChange={handlePODirectionChange}
								>
									<SelectTrigger className="w-36">
										<SelectValue placeholder="All orders" />
									</SelectTrigger>
									<SelectContent>
										{getPurchaseOrderDirectionOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Select
									value={poStatusFilter ?? "all"}
									items={[
										{ value: "all", label: "All statuses" },
										...getPurchaseOrderStatusOptions(),
									]}
									onValueChange={handlePOStatusChange}
								>
									<SelectTrigger className="w-36">
										<SelectValue placeholder="All statuses" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All statuses</SelectItem>
										{getPurchaseOrderStatusOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Can scope="supplierPurchaseOrders.create">
									<ActionHeaderButton
										label="New purchase order"
										onClick={() => setCreatePurchaseOrderModalOpen(true)}
									/>
								</Can>
							</div>
						)}

						{tab === "supplierOrders" && (
							<div className="flex items-center gap-3">
								<Select
									value={soStatusFilter ?? "all"}
									items={[
										{ value: "all", label: "All statuses" },
										...getSupplierOrderStatusOptions(),
									]}
									onValueChange={handleSOStatusChange}
								>
									<SelectTrigger className="w-44">
										<SelectValue placeholder="All statuses" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">All statuses</SelectItem>
										{getSupplierOrderStatusOptions().map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Can scope="supplierPurchaseOrders.create">
									<ActionHeaderButton
										label="New supplier order"
										onClick={() => setCreateSupplierOrderModalOpen(true)}
									/>
								</Can>
							</div>
						)}
					</div>

					<TabsContent value="stock" className="flex flex-1 flex-col">
						<div className="flex flex-1 flex-col gap-4 sm:gap-6">
							{/* Search and Display Select */}
							<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
								<div className="flex-1 sm:max-w-sm">
									<SearchInput
										placeholder="Search products, SKUs, rooms..."
										value={searchQuery}
										onChange={(e) => handleSearchChange(e.target.value)}
									/>
								</div>
								<DisplaySelect
									viewMode={displaySettings.viewMode}
									onViewModeChange={(mode) => updateSetting("viewMode", mode)}
									selectedSort={displaySettings.sortKey}
									onSortChange={(sort) => updateSetting("sortKey", sort)}
									sortOptions={[
										{ value: "name", label: "Product Name" },
										{ value: "quantity", label: "Quantity" },
										{ value: "value", label: "Estimated Value" },
										{ value: "expiry", label: "Expiry Date" },
									]}
									options={[
										{
											key: "showHeaders",
											label: "Show table headers",
											checked: displaySettings.showHeaders,
											icon: <LayoutPanelTop className="size-4" />,
										},
									]}
									onOptionsChange={(key, checked) => {
										if (key === "showHeaders")
											updateSetting("showHeaders", checked);
									}}
									properties={[
										{
											key: "room",
											label: "Room",
											visible: displaySettings.columnVisibility.room,
										},
										{
											key: "quantity",
											label: "Quantity",
											visible: displaySettings.columnVisibility.quantity,
										},
										{
											key: "unitCost",
											label: "Unit Cost",
											visible: displaySettings.columnVisibility.unitCost,
										},
										{
											key: "estValue",
											label: "Est. Value",
											visible: displaySettings.columnVisibility.estValue,
										},
										{
											key: "expiry",
											label: "Expiry",
											visible: displaySettings.columnVisibility.expiry,
										},
										{
											key: "status",
											label: "Status",
											visible: displaySettings.columnVisibility.status,
										},
									]}
									onPropertiesChange={updateColumnVisibility}
									onReset={handleReset}
									onSetDefault={handleSetDefault}
									showFooter={hasChanges}
								/>
							</div>

							{/* Inventory Table */}
							{!isLoadingStocks && !!branchId && filteredStocks.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No stock at this branch"
										description="Products in your catalog haven't been stocked here yet. Create a restock to start receiving inventory."
										actionLabel="Receive Stock"
										onAction={() => setCreateRestockModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={Archive01Icon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<InventoryTable
									data={paginatedStocks}
									loading={isLoadingStocks}
									pagination={pagination}
									onPaginationChange={onPaginationChange}
									rowCount={filteredStocks.length}
									onAdjust={handleAdjust}
									onTransfer={handleTransfer}
									onViewHistory={handleViewHistory}
									viewMode={displaySettings.viewMode}
									showHeaders={displaySettings.showHeaders}
									columnVisibility={displaySettings.columnVisibility}
									showPagination={true}
								/>
							)}
						</div>
					</TabsContent>

					<TabsContent value="restocks" className="flex flex-1 flex-col">
						<div className="flex flex-1 flex-col gap-4 sm:gap-6">
							{!isLoadingRestocks && !!branchId && restocks.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No restocks"
										description="Create restocks to track incoming stock at this branch"
										actionLabel="Create restock"
										onAction={() => setCreateRestockModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={DeliveryBox01Icon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<RestocksTable
									data={restocks}
									loading={isLoadingRestocks}
									pagination={pagination}
									onPaginationChange={onPaginationChange}
									rowCount={restocks.length}
									onView={handleViewRestock}
									onEdit={handleEditRestock}
									onComplete={handleCompleteRestock}
									onCancel={handleCancelRestock}
									onDelete={handleDeleteRestock}
								/>
							)}
						</div>
					</TabsContent>

					<TabsContent value="reconciliations" className="flex flex-1 flex-col">
						<div className="flex flex-1 flex-col gap-4 sm:gap-6">
							{!isLoadingReconciliations &&
							!!branchId &&
							reconciliations.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No reconciliations"
										description="Start a count to reconcile stock at this branch"
										actionLabel="Start count"
										onAction={() => setStartReconciliationModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={File02Icon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<ReconciliationsTable
									data={reconciliations}
									loading={isLoadingReconciliations}
									pagination={pagination}
									onPaginationChange={onPaginationChange}
									rowCount={reconciliations.length}
									onView={handleViewReconciliation}
									onContinue={handleContinueReconciliation}
									onSubmit={handleSubmitReconciliation}
									onApprove={handleApproveReconciliation}
									onReject={handleRejectReconciliation}
								/>
							)}
						</div>
					</TabsContent>

					<TabsContent value="transfers" className="flex flex-1 flex-col">
						<div className="flex flex-1 flex-col gap-4 sm:gap-6">
							{!isLoadingTransfers &&
							!!branchId &&
							transferOrders.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No transfers"
										description="Transfer orders between branches will appear here"
										actionLabel="New transfer"
										onAction={() => setCreateTransferModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={DeliveryTruck02Icon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<TransferOrdersTable
									data={transferOrders}
									currentBranchId={branchId as Id<"branches">}
									loading={isLoadingTransfers}
									pagination={pagination}
									onPaginationChange={onPaginationChange}
									rowCount={transferOrders.length}
									onView={handleViewTransfer}
									onApprove={handleApproveTransfer}
									onShip={handleShipTransfer}
									onReceive={handleReceiveTransfer}
									onComplete={handleCompleteTransfer}
									onCancel={handleCancelTransfer}
									onDelete={handleDeleteTransfer}
								/>
							)}
						</div>
					</TabsContent>

					<TabsContent value="purchaseOrders" className="flex flex-1 flex-col">
						<div className="flex flex-1 flex-col gap-4 sm:gap-6">
							{!isLoadingPurchaseOrders &&
							!!branchId &&
							purchaseOrders.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No purchase orders"
										description="Purchase orders between branches will appear here"
										actionLabel="New purchase order"
										onAction={() => setCreatePurchaseOrderModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={ShoppingBasket01Icon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<PurchaseOrdersTable
									data={purchaseOrders}
									currentBranchId={branchId as Id<"branches">}
									loading={isLoadingPurchaseOrders}
									pagination={pagination}
									onPaginationChange={onPaginationChange}
									rowCount={purchaseOrders.length}
									onView={handleViewPurchaseOrder}
									onSubmit={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleSubmitPurchaseOrder(original);
									}}
									onApprove={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleApprovePurchaseOrder(original);
									}}
									onReject={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleRejectPurchaseOrder(original);
									}}
									onMarkFulfilling={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleMarkFulfilling(original);
									}}
									onMarkFulfilled={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleMarkFulfilled(original);
									}}
									onCancel={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleCancelPurchaseOrder(original);
									}}
									onDelete={(po) => {
										const original = (
											purchaseOrdersQueryResult?.page ?? []
										).find((o) => (o._id as string) === (po._id as string));
										if (original) handleDeletePurchaseOrder(original);
									}}
								/>
							)}
						</div>
					</TabsContent>

					<TabsContent value="supplierOrders" className="flex flex-1 flex-col">
						<div className="flex flex-1 flex-col gap-4 sm:gap-6">
							{!isLoadingSupplierOrders &&
							!!branchId &&
							supplierOrders.length === 0 ? (
								<div className="flex flex-1 flex-col justify-end pb-12">
									<EmptyStateWithSkeleton
										title="No supplier orders"
										description="Supplier orders for this branch will appear here"
										actionLabel="New supplier order"
										onAction={() => setCreateSupplierOrderModalOpen(true)}
										icon={
											<HugeiconsIcon
												icon={DeliveryBox01Icon}
												className="h-5 w-5 text-neutral-700"
												strokeWidth={2.2}
											/>
										}
									/>
								</div>
							) : (
								<SupplierOrdersTable
									data={supplierOrders}
									loading={isLoadingSupplierOrders}
									pagination={pagination}
									onPaginationChange={onPaginationChange}
									rowCount={supplierOrders.length}
									onView={handleViewSupplierOrder}
									onSubmit={handleSubmitSupplierOrder}
									onConfirm={handleConfirmSupplierOrder}
									onReceive={handleReceiveSupplierOrder}
									onCancel={handleCancelSupplierOrder}
									onDelete={handleDeleteSupplierOrder}
								/>
							)}
						</div>
					</TabsContent>
				</Tabs>
			)}

			{/* Stock Modals */}
			<AdjustStockModal
				open={adjustModalOpen}
				onOpenChange={setAdjustModalOpen}
				stock={selectedStock}
				onSubmit={handleAdjustSubmit}
				isSubmitting={isAdjusting}
			/>

			<TransferStockModal
				open={transferModalOpen}
				onOpenChange={setTransferModalOpen}
				stock={selectedStock}
				rooms={rooms}
				onSubmit={handleTransferSubmit}
				isSubmitting={isTransferring}
			/>

			<MovementHistoryDrawer
				open={historyDrawerOpen}
				onOpenChange={setHistoryDrawerOpen}
				stock={selectedStock}
				movements={movements}
				loading={isLoadingMovements}
			/>

			{/* Restock Modals */}
			<CreateRestockModal
				open={createRestockModalOpen}
				onOpenChange={setCreateRestockModalOpen}
				suppliers={suppliers}
				onSubmit={handleCreateRestockSubmit}
				isSubmitting={isCreatingRestock}
			/>

			<EditRestockModal
				open={editRestockModalOpen}
				onOpenChange={setEditRestockModalOpen}
				restock={selectedRestock}
				suppliers={suppliers}
				onSubmit={handleEditRestockSubmit}
				isSubmitting={isEditingRestock}
			/>

			<RestockDetailDrawer
				open={restockDetailDrawerOpen}
				onOpenChange={setRestockDetailDrawerOpen}
				restock={selectedRestock}
				items={restockItems}
				products={products}
				rooms={rooms}
				loading={isLoadingRestockItems}
				onAddItem={handleAddRestockItem}
				onEditItem={handleEditRestockItem}
				onRemoveItem={handleRemoveRestockItem}
				onComplete={() =>
					selectedRestock && handleCompleteRestock(selectedRestock)
				}
				onCancel={() => selectedRestock && handleCancelRestock(selectedRestock)}
				onDelete={() => selectedRestock && handleDeleteRestock(selectedRestock)}
				isCompleting={isCompletingRestock}
				isCancelling={isCancellingRestock}
				isDeleting={isDeletingRestock}
			/>

			<AddRestockItemModal
				open={addItemModalOpen}
				onOpenChange={setAddItemModalOpen}
				productsWithStock={productsWithStock}
				rooms={rooms}
				onSubmit={handleAddItemSubmit}
				isSubmitting={isAddingItem}
				isLoading={isLoadingProducts}
			/>

			<EditRestockItemModal
				open={editItemModalOpen}
				onOpenChange={setEditItemModalOpen}
				item={selectedRestockItem}
				products={products}
				rooms={rooms}
				onSubmit={handleEditItemSubmit}
				isSubmitting={isEditingItem}
			/>

			{/* Reconciliation Modals */}
			<StartReconciliationModal
				open={startReconciliationModalOpen}
				onOpenChange={setStartReconciliationModalOpen}
				rooms={rooms}
				onSubmit={handleStartReconciliationSubmit}
				isSubmitting={isStartingReconciliation}
			/>

			<ReconciliationDetailDrawer
				open={reconciliationDetailDrawerOpen}
				onOpenChange={setReconciliationDetailDrawerOpen}
				reconciliation={selectedReconciliation}
				items={reconciliationItems}
				loading={isLoadingReconciliationItems}
				onRecordCount={handleRecordCount}
				onSubmit={() =>
					selectedReconciliation &&
					handleSubmitReconciliation(selectedReconciliation)
				}
				onApprove={() =>
					selectedReconciliation &&
					handleApproveReconciliation(selectedReconciliation)
				}
				onReject={() =>
					selectedReconciliation &&
					handleRejectReconciliation(selectedReconciliation)
				}
				isSubmitting={isSubmittingReconciliation}
				isApproving={isApprovingReconciliation}
				isRejecting={isRejectingReconciliation}
			/>

			<RecordCountModal
				open={recordCountModalOpen}
				onOpenChange={setRecordCountModalOpen}
				item={selectedReconciliationItem}
				onSubmit={handleRecordCountSubmit}
				isSubmitting={isRecordingCount}
			/>

			<RejectReconciliationModal
				open={rejectReconciliationModalOpen}
				onOpenChange={setRejectReconciliationModalOpen}
				onSubmit={handleRejectReconciliationSubmit}
				isSubmitting={isRejectingReconciliation}
			/>

			{/* Transfer Order Modals */}
			<CreateTransferOrderModal
				open={createTransferModalOpen}
				onOpenChange={setCreateTransferModalOpen}
				currentBranchId={branchId as Id<"branches">}
				branches={branches}
				sourceRooms={rooms}
				destinationRooms={destinationRooms}
				selectedDestinationBranchId={selectedDestinationBranchId}
				onDestinationBranchChange={setSelectedDestinationBranchId}
				onSubmit={handleCreateTransferSubmit}
				isSubmitting={isCreatingTransfer}
			/>

			<TransferOrderDetailDrawer
				open={transferDetailDrawerOpen}
				onOpenChange={setTransferDetailDrawerOpen}
				transfer={selectedTransfer}
				items={transferItems}
				currentBranchId={branchId as Id<"branches">}
				loading={isLoadingTransferItems}
				onAddItem={handleAddTransferItem}
				onRemoveItem={handleRemoveTransferItem}
				onApprove={() =>
					selectedTransfer && handleApproveTransfer(selectedTransfer)
				}
				onShip={() => selectedTransfer && handleShipTransfer(selectedTransfer)}
				onReceive={() =>
					selectedTransfer && handleReceiveTransfer(selectedTransfer)
				}
				onComplete={() =>
					selectedTransfer && handleCompleteTransfer(selectedTransfer)
				}
				onCancel={() =>
					selectedTransfer && handleCancelTransfer(selectedTransfer)
				}
				onDelete={() =>
					selectedTransfer && handleDeleteTransfer(selectedTransfer)
				}
				isApproving={isApprovingTransfer}
				isShipping={isShippingTransfer}
				isReceiving={isReceivingTransfer}
				isCompleting={isCompletingTransfer}
				isCancelling={isCancellingTransfer}
				isDeleting={isDeletingTransfer}
			/>

			<AddTransferItemModal
				open={addTransferItemModalOpen}
				onOpenChange={setAddTransferItemModalOpen}
				products={products}
				stocks={sourceRoomStocksData}
				onSubmit={handleAddTransferItemSubmit}
				isSubmitting={isAddingTransferItem}
			/>

			{/* Purchase Order Modals */}
			<CreatePurchaseOrderModal
				open={createPurchaseOrderModalOpen}
				onOpenChange={setCreatePurchaseOrderModalOpen}
				suppliers={suppliers}
				onSubmit={handleCreatePurchaseOrderSubmit}
				isSubmitting={isCreatingPurchaseOrder}
			/>

			<PurchaseOrderDetailDrawer
				open={purchaseOrderDetailDrawerOpen}
				onOpenChange={setPurchaseOrderDetailDrawerOpen}
				purchaseOrder={selectedPurchaseOrder as unknown as SupplierOrder | null}
				items={purchaseOrderItems}
				currentBranchId={branchId as Id<"branches">}
				loading={isLoadingPurchaseOrderItems}
				onAddItem={handleAddPurchaseOrderItem}
				onRemoveItem={handleRemovePurchaseOrderItem}
				onSubmit={() =>
					selectedPurchaseOrder &&
					handleSubmitPurchaseOrder(selectedPurchaseOrder)
				}
				onApprove={() =>
					selectedPurchaseOrder &&
					handleApprovePurchaseOrder(selectedPurchaseOrder)
				}
				onReject={() =>
					selectedPurchaseOrder &&
					handleRejectPurchaseOrder(selectedPurchaseOrder)
				}
				onMarkFulfilling={() =>
					selectedPurchaseOrder && handleMarkFulfilling(selectedPurchaseOrder)
				}
				onMarkFulfilled={() =>
					selectedPurchaseOrder && handleMarkFulfilled(selectedPurchaseOrder)
				}
				onCancel={() =>
					selectedPurchaseOrder &&
					handleCancelPurchaseOrder(selectedPurchaseOrder)
				}
				onDelete={() =>
					selectedPurchaseOrder &&
					handleDeletePurchaseOrder(selectedPurchaseOrder)
				}
				isSubmitting={isSubmittingPurchaseOrder}
				isApproving={isApprovingPurchaseOrder}
				isRejecting={isRejectingPurchaseOrder}
				isMarkingFulfilling={isMarkingFulfilling}
				isMarkingFulfilled={isMarkingFulfilled}
				isCancelling={isCancellingPurchaseOrder}
				isDeleting={isDeletingPurchaseOrder}
			/>

			<AddPurchaseOrderItemModal
				open={addPurchaseOrderItemModalOpen}
				onOpenChange={setAddPurchaseOrderItemModalOpen}
				productsWithStock={productsWithStock}
				onSubmit={handleAddPurchaseOrderItemSubmit}
				isSubmitting={isAddingPurchaseOrderItem}
				isLoading={isLoadingProducts}
			/>

			<RejectPurchaseOrderModal
				open={rejectPurchaseOrderModalOpen}
				onOpenChange={setRejectPurchaseOrderModalOpen}
				onSubmit={handleRejectPurchaseOrderSubmit}
				isSubmitting={isRejectingPurchaseOrder}
			/>

			{/* Supplier Order Modals */}
			<CreateSupplierOrderModal
				open={createSupplierOrderModalOpen}
				onOpenChange={setCreateSupplierOrderModalOpen}
				suppliers={suppliers}
				onSubmit={handleCreateSupplierOrderSubmit}
				isSubmitting={isCreatingSupplierOrder}
			/>

			<SupplierOrderDetailDrawer
				open={supplierOrderDetailDrawerOpen}
				onOpenChange={setSupplierOrderDetailDrawerOpen}
				supplierOrder={selectedSupplierOrder}
				items={supplierOrderItems}
				loading={isLoadingSupplierOrderItems}
				onAddItem={handleAddSupplierOrderItem}
				onEditItem={handleEditSupplierOrderItem}
				onRemoveItem={handleRemoveSupplierOrderItem}
				onSubmit={() =>
					selectedSupplierOrder &&
					handleSubmitSupplierOrder(selectedSupplierOrder)
				}
				onConfirm={() =>
					selectedSupplierOrder &&
					handleConfirmSupplierOrder(selectedSupplierOrder)
				}
				onReceive={() =>
					selectedSupplierOrder &&
					handleReceiveSupplierOrder(selectedSupplierOrder)
				}
				onCancel={() =>
					selectedSupplierOrder &&
					handleCancelSupplierOrder(selectedSupplierOrder)
				}
				onDelete={() =>
					selectedSupplierOrder &&
					handleDeleteSupplierOrder(selectedSupplierOrder)
				}
				isSubmitting={isSubmittingSupplierOrder}
				isConfirming={isConfirmingSupplierOrder}
				isReceiving={isReceivingSupplierOrder}
				isCancelling={isCancellingSupplierOrder}
				isDeleting={isDeletingSupplierOrder}
			/>

			<AddSupplierOrderItemModal
				open={addSupplierOrderItemModalOpen}
				onOpenChange={setAddSupplierOrderItemModalOpen}
				productsWithStock={productsWithStock}
				onSubmit={handleAddSupplierOrderItemSubmit}
				isSubmitting={isAddingSupplierOrderItem}
				isLoading={isLoadingProducts}
			/>

			<EditSupplierOrderItemModal
				open={editSupplierOrderItemModalOpen}
				onOpenChange={setEditSupplierOrderItemModalOpen}
				item={selectedSupplierOrderItem}
				onSubmit={handleEditSupplierOrderItemSubmit}
				isSubmitting={isEditingSupplierOrderItem}
			/>
		</PageContent>
	);
}
